<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import VarsityFeedbackLoading from "@/components/feedback/VarsityFeedbackLoading.vue"
import type { PosPaymentDataDto } from "@/model/order/pos-payment-data-dto"
import type { PosTerminalDto } from "@/model/order/pos-terminal-dto"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, useHttpClient, useHttpGet, useLoadingState } from "@/vf"
import { useLocalStorage } from "@vueuse/core"
import { computed, ref, watch } from "vue"

const props = defineProps<{
    orderProcessId: string
    dataEndpoint?: string
    createEndpoint?: string
}>()

const emit = defineEmits<{
    // Emitted once the terminal payment has been started; carries the created payment's id.
    (e: "paid", paymentId: string): void
}>()

const trans = defineTranslations({
    title: {
        de: "POS-Zahlung",
        en: "POS Payment",
    },
    pending_amount: {
        de: "Offener Betrag",
        en: "Pending amount",
    },
    terminal: {
        de: "Terminal",
        en: "Terminal",
    },
    terminal_placeholder: {
        de: "Terminal auswählen",
        en: "Select terminal",
    },
    action: {
        de: "POS-Zahlung erstellen",
        en: "Create POS payment",
    },
    info_no_amount: {
        de: "Der Auftrag ist bereits vollständig bezahlt.",
        en: "The order is already fully paid.",
    },
    info_no_terminals: {
        de: "Keine aktiven Mollie-Terminals gefunden.",
        en: "No active Mollie terminals found.",
    },
    success: {
        de: "POS-Zahlung wurde erstellt. Bitte Zahlung am Terminal abschließen.",
        en: "POS payment was created. Please complete the payment on the terminal.",
    },
    error: {
        de: "POS-Zahlung konnte nicht erstellt werden.",
        en: "POS payment could not be created.",
    },
})

const http = useHttpClient()
const dataEndpoint = computed(() => {
    return props.dataEndpoint ?? `/order/confirmation/${props.orderProcessId}/pos-data`
})
const createEndpoint = computed(() => {
    return props.createEndpoint ?? `/order/confirmation/${props.orderProcessId}/pos-payment`
})
const { data, loading, refresh } = useHttpGet<PosPaymentDataDto>(
    dataEndpoint,
    false,
)
const createPaymentLoading = useLoadingState()
// Remember the last chosen terminal across orders/sessions and pre-select it when available.
const selectedTerminalId = useLocalStorage<string | null>("pos.lastTerminalId", null)
const successMessage = ref<string>()
const errorMessage = ref<string>()

watch(
    () => data.value?.terminals,
    terminals => {
        if (!terminals?.length) {
            // Keep the stored preference; the select isn't shown without terminals anyway.
            return
        }

        // Keep the current/remembered terminal if it's still available, otherwise fall back to the first.
        if (selectedTerminalId.value && terminals.some(terminal => terminal.id === selectedTerminalId.value)) {
            return
        }

        selectedTerminalId.value = terminals[0].id
    },
    { immediate: true },
)

const enableSubmit = computed(() => {
    return !!selectedTerminalId.value && (data.value?.pendingAmount ?? 0) > 0 && (data.value?.terminals.length ?? 0) > 0
})

function getTerminalLabel(terminal: PosTerminalDto): string {
    const details = [terminal.description, terminal.brand, terminal.model].filter(Boolean)
    if (details.length === 0) {
        return terminal.id
    }

    return `${details.join(" - ")} (${terminal.id})`
}

async function createPosPayment() {
    successMessage.value = undefined
    errorMessage.value = undefined

    if (!enableSubmit.value || !selectedTerminalId.value) {
        return
    }

    let paymentId = ""
    try {
        await createPaymentLoading.while(async () => {
            const response = await http.post<{ id: string; molliePaymentId: string | null }>(createEndpoint.value, {
                terminalId: selectedTerminalId.value,
            })
            paymentId = response.data.id
        })
    } catch {
        errorMessage.value = trans("error")
        return
    }

    successMessage.value = trans("success")
    await refresh()
    emit("paid", paymentId)
}
</script>

<template>
    <div>
        <VarsityFeedbackLoading v-if="loading" class="my-5" />
        <div v-if="!loading && data?.canCreatePosPayments">
            <div class="bg-primary-dark text-white px-4 py-2 font-weight-bold">
                {{ trans("title") }}
            </div>

            <div class="p-4 border border-gray border-top-0">
                <table class="table table-borderless mb-0">
                    <tbody>
                        <tr>
                            <th>{{ trans("pending_amount") }}</th>
                            <td class="text-right">
                                {{ $currency(data.pendingAmount, 100, { currencyDisplay: "name" }) }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-if="data.pendingAmount <= 0" class="alert alert-success mb-0">
                    {{ trans("info_no_amount") }}
                </div>

                <div v-else-if="data.terminals.length === 0" class="alert alert-warning mb-0">
                    {{ trans("info_no_terminals") }}
                </div>

                <div v-else class="px-2">
                    <b class="d-block mb-2">{{ trans("terminal") }}</b>
                    <select v-model="selectedTerminalId" class="form-control">
                        <option :value="undefined" disabled>
                            {{ trans("terminal_placeholder") }}
                        </option>
                        <option v-for="terminal in data.terminals" :key="terminal.id" :value="terminal.id">
                            {{ getTerminalLabel(terminal) }}
                        </option>
                    </select>

                    <div v-if="successMessage" class="alert alert-success mt-3 mb-0">
                        {{ successMessage }}
                    </div>

                    <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">
                        {{ errorMessage }}
                    </div>

                    <div class="mt-4 text-right">
                        <LoadingButton
                            :loading="createPaymentLoading"
                            :disabled="!enableSubmit"
                            @click="createPosPayment"
                        >
                            {{ trans("action") }}
                        </LoadingButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
